import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Field } from "./Orders";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import {
    UserCircle,
    Buildings,
    Palette,
    Check,
    LockKey,
    EnvelopeSimple,
    Phone,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
    { key: "profile", label: "Profile", icon: UserCircle },
    { key: "company", label: "Company", icon: Buildings },
    { key: "appearance", label: "Appearance", icon: Palette },
];

const THEMES = [
    { key: "blue", label: "Ocean Blue", swatch: "#2563eb" },
    { key: "violet", label: "Royal Violet", swatch: "#7c3aed" },
    { key: "emerald", label: "Emerald", swatch: "#059669" },
    { key: "rose", label: "Crimson Rose", swatch: "#e11d48" },
    { key: "amber", label: "Amber Glow", swatch: "#d97706" },
    { key: "cyan", label: "Electric Cyan", swatch: "#0891b2" },
];

export default function Settings() {
    const [tab, setTab] = useState("profile");
    const { user } = useAuth();
    return (
        <div className="space-y-6" data-testid="settings-page">
            <div>
                <div className="label-uppercase">Configuration</div>
                <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                    Settings
                </h1>
                <p className="text-slate-400 text-sm mt-1">
                    Manage your account, company information and workspace appearance.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4 lg:gap-6">
                <aside className="flex lg:block gap-2 overflow-x-auto lg:overflow-visible -mx-1 px-1 lg:mx-0 lg:px-0 space-x-1 lg:space-x-0 lg:space-y-1 pb-1 lg:pb-0">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`settings-tab-${t.key}`}
                            className={`shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 py-2 lg:py-2.5 rounded-xl text-sm transition whitespace-nowrap ${
                                tab === t.key
                                    ? "bg-white/8 border border-white/10 text-white"
                                    : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                            }`}
                        >
                            <t.icon size={18} weight="duotone" />
                            {t.label}
                        </button>
                    ))}
                </aside>

                <div className="min-w-0">
                    {tab === "profile" && <ProfilePanel />}
                    {tab === "company" && <CompanyPanel readOnly={user?.role !== "admin"} />}
                    {tab === "appearance" && <AppearancePanel readOnly={user?.role !== "admin"} />}
                </div>
            </div>
        </div>
    );
}

function ProfilePanel() {
    const { user, setUser } = useAuth();
    const [profile, setProfile] = useState({ name: "", email: "" });
    const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPwd, setSavingPwd] = useState(false);

    useEffect(() => {
        if (user) setProfile({ name: user.name || "", email: user.email || "" });
    }, [user]);

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            const { data } = await api.put("/auth/profile", profile);
            setUser(data);
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
        } finally {
            setSavingProfile(false);
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (pwd.new_password !== pwd.confirm) {
            toast.error("New passwords do not match");
            return;
        }
        if (pwd.new_password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setSavingPwd(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pwd.current_password,
                new_password: pwd.new_password,
            });
            setPwd({ current_password: "", new_password: "", confirm: "" });
            toast.success("Password changed");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed");
        } finally {
            setSavingPwd(false);
        }
    };

    return (
        <div className="space-y-6">
            <section className="glass-card p-6" data-testid="profile-panel">
                <div className="flex items-center gap-2 mb-1">
                    <UserCircle size={18} weight="duotone" />
                    <h2 className="font-display text-lg font-semibold">Personal details</h2>
                </div>
                <p className="text-xs text-slate-500 mb-5">
                    Update your display name and login email.
                </p>
                <form onSubmit={saveProfile} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Name" required>
                            <input
                                required
                                className="input"
                                value={profile.name}
                                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                data-testid="profile-name"
                            />
                        </Field>
                        <Field label="Email" required>
                            <input
                                type="email"
                                required
                                className="input"
                                value={profile.email}
                                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                data-testid="profile-email"
                            />
                        </Field>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={savingProfile}
                            data-testid="save-profile"
                            className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow text-sm font-medium disabled:opacity-60"
                        >
                            {savingProfile ? "Saving…" : "Save profile"}
                        </button>
                    </div>
                </form>
            </section>

            <section className="glass-card p-6" data-testid="password-panel">
                <div className="flex items-center gap-2 mb-1">
                    <LockKey size={18} weight="duotone" />
                    <h2 className="font-display text-lg font-semibold">Change password</h2>
                </div>
                <p className="text-xs text-slate-500 mb-5">
                    Use at least 6 characters. You will stay signed in after the change.
                </p>
                <form onSubmit={savePassword} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Current password" required>
                            <input
                                type="password"
                                required
                                className="input"
                                value={pwd.current_password}
                                onChange={(e) =>
                                    setPwd({ ...pwd, current_password: e.target.value })
                                }
                                data-testid="current-password"
                            />
                        </Field>
                        <Field label="New password" required>
                            <input
                                type="password"
                                required
                                className="input"
                                value={pwd.new_password}
                                onChange={(e) =>
                                    setPwd({ ...pwd, new_password: e.target.value })
                                }
                                data-testid="new-password"
                            />
                        </Field>
                        <Field label="Confirm new password" required>
                            <input
                                type="password"
                                required
                                className="input"
                                value={pwd.confirm}
                                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                                data-testid="confirm-password"
                            />
                        </Field>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={savingPwd}
                            data-testid="save-password"
                            className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow text-sm font-medium disabled:opacity-60"
                        >
                            {savingPwd ? "Updating…" : "Change password"}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}

function CompanyPanel({ readOnly }) {
    const { settings, update } = useSettings();
    const [form, setForm] = useState(settings);
    const [saving, setSaving] = useState(false);

    useEffect(() => setForm(settings), [settings]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await update({
                company_name: form.company_name,
                company_email: form.company_email,
                company_phone: form.company_phone,
                company_address: form.company_address,
                gst_number: form.gst_number,
                upi_id: form.upi_id,
                website: form.website,
                founder_name: form.founder_name,
                footer_signature: form.footer_signature,
            });
            toast.success("Company details saved");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="glass-card p-6" data-testid="company-panel">
            <div className="flex items-center gap-2 mb-1">
                <Buildings size={18} weight="duotone" />
                <h2 className="font-display text-lg font-semibold">Company details</h2>
            </div>
            <p className="text-xs text-slate-500 mb-5">
                Shown on invoices, the app footer and WhatsApp share messages.
                {readOnly && " (admin-only — contact your admin to edit)"}
            </p>

            <form onSubmit={save} className="space-y-4">
                <fieldset disabled={readOnly} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Company name" required>
                            <input
                                required
                                className="input"
                                value={form.company_name || ""}
                                onChange={(e) =>
                                    setForm({ ...form, company_name: e.target.value })
                                }
                                data-testid="company-name"
                            />
                        </Field>
                        <Field label="Founder name">
                            <input
                                className="input"
                                value={form.founder_name || ""}
                                onChange={(e) =>
                                    setForm({ ...form, founder_name: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Email">
                            <input
                                type="email"
                                className="input"
                                value={form.company_email || ""}
                                onChange={(e) =>
                                    setForm({ ...form, company_email: e.target.value })
                                }
                                data-testid="company-email"
                            />
                        </Field>
                        <Field label="Contact number">
                            <input
                                className="input"
                                value={form.company_phone || ""}
                                onChange={(e) =>
                                    setForm({ ...form, company_phone: e.target.value })
                                }
                                data-testid="company-phone"
                            />
                        </Field>
                        <Field label="GSTIN">
                            <input
                                className="input"
                                value={form.gst_number || ""}
                                onChange={(e) =>
                                    setForm({ ...form, gst_number: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="UPI ID">
                            <input
                                className="input"
                                value={form.upi_id || ""}
                                onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                            />
                        </Field>
                        <Field label="Website">
                            <input
                                className="input"
                                value={form.website || ""}
                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                            />
                        </Field>
                        <Field label="Footer signature">
                            <input
                                className="input"
                                value={form.footer_signature || ""}
                                onChange={(e) =>
                                    setForm({ ...form, footer_signature: e.target.value })
                                }
                            />
                        </Field>
                    </div>
                    <Field label="Address">
                        <textarea
                            rows={2}
                            className="input"
                            value={form.company_address || ""}
                            onChange={(e) =>
                                setForm({ ...form, company_address: e.target.value })
                            }
                        />
                    </Field>
                </fieldset>

                {!readOnly && (
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="save-company"
                            className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow text-sm font-medium disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save company details"}
                        </button>
                    </div>
                )}
            </form>

            <div className="mt-6 pt-5 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                    <EnvelopeSimple size={16} className="text-slate-500" />
                    <span className="font-mono text-xs">
                        {settings.company_email || "—"}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                    <Phone size={16} className="text-slate-500" />
                    <span className="font-mono text-xs">{settings.company_phone || "—"}</span>
                </div>
            </div>
        </section>
    );
}

function AppearancePanel({ readOnly }) {
    const { settings, update } = useSettings();
    const [active, setActive] = useState(settings.theme || "blue");

    useEffect(() => setActive(settings.theme || "blue"), [settings.theme]);

    const pick = async (key) => {
        if (readOnly) return;
        setActive(key);
        // Optimistic theme apply
        document.documentElement.setAttribute("data-theme", key);
        try {
            await update({ theme: key });
            toast.success(`Theme set to ${key}`);
        } catch {
            toast.error("Failed to save theme");
        }
    };

    return (
        <section className="glass-card p-6" data-testid="appearance-panel">
            <div className="flex items-center gap-2 mb-1">
                <Palette size={18} weight="duotone" />
                <h2 className="font-display text-lg font-semibold">Theme colour</h2>
            </div>
            <p className="text-xs text-slate-500 mb-5">
                Choose your workspace accent. Applied instantly across the app.
                {readOnly && " (admin-only)"}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {THEMES.map((t) => (
                    <button
                        type="button"
                        key={t.key}
                        disabled={readOnly}
                        onClick={() => pick(t.key)}
                        data-testid={`theme-${t.key}`}
                        className={`relative group rounded-2xl p-4 border transition text-left ${
                            active === t.key
                                ? "border-white/30 bg-white/[0.06]"
                                : "border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]"
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                        <div
                            className="h-14 w-full rounded-xl"
                            style={{
                                background: `linear-gradient(135deg, ${t.swatch} 0%, ${t.swatch}88 60%, transparent 100%)`,
                                boxShadow: `0 8px 24px ${t.swatch}55`,
                            }}
                        />
                        <div className="mt-3 flex items-center justify-between">
                            <span className="text-sm font-medium">{t.label}</span>
                            {active === t.key && (
                                <Check size={16} weight="bold" className="text-emerald-300" />
                            )}
                        </div>
                    </button>
                ))}
            </div>
        </section>
    );
}
